// micCue — the short "mic is open, talk now" blip.
//
// Synthesised rather than shipped as an audio file: it is a 200ms tone, and a
// binary asset would mean an LFS-tracked file plus a network fetch that can
// lose the race with the click that triggered it. One oscillator with a pitch
// glide has neither problem and is tunable in place.
//
// The AudioContext is INJECTED, never created here. The app has exactly one
// (AudioPlayer's), and it is load-bearing: sentence playback drives
// onSentenceCompleted off its `onended`, so a second context would be both
// wasteful and a second thing to keep un-suspended. Injection also makes this
// unit-testable with a stub — no WebAudio in node.
//
// Deliberately silent (returns false) unless the context is actually running:
// scheduling onto a suspended context produces no sound and leaks nodes that
// only fire once someone resumes it. Callers play this from a click, which is
// a user gesture, so 'running' is the normal case.

/** Peak gain of the cue. A confirmation blip, not an alert — keep it under the avatar's voice. */
export const CUE_GAIN = 0.06
const START_HZ = 660
const END_HZ = 990
const GLIDE_S = 0.09
const TAIL_S = 0.2

/**
 * Play the mic-ready cue on an existing AudioContext.
 *
 * @param {AudioContext} ctx  the app's shared context (audioPlayer.ctx)
 * @param {{gain?: number}} [opts]
 * @returns {boolean} true when the cue was actually scheduled
 */
export function playMicReadyCue(ctx, { gain = CUE_GAIN } = {}) {
  if (!ctx || typeof ctx.createOscillator !== 'function' || typeof ctx.createGain !== 'function') return false
  // Only 'running' makes a sound. 'suspended'/'interrupted' would queue a tone
  // that fires at some arbitrary later resume — worse than staying quiet.
  if (ctx.state !== 'running') return false

  const t0 = ctx.currentTime
  const env = ctx.createGain()
  // Ramp up over ~12ms rather than starting at full gain: an instant edge on a
  // sine is an audible click.
  env.gain.setValueAtTime(0, t0)
  env.gain.linearRampToValueAtTime(gain, t0 + 0.012)
  // exponential can never reach 0, hence the epsilon target.
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + TAIL_S * 0.9)
  env.connect(ctx.destination)

  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(START_HZ, t0)
  osc.frequency.exponentialRampToValueAtTime(END_HZ, t0 + GLIDE_S)
  osc.connect(env)
  osc.start(t0)
  osc.stop(t0 + TAIL_S)
  // Release the nodes; without this every activation leaves a dead subgraph
  // attached to a context that lives as long as the page.
  osc.onended = () => {
    try { osc.disconnect() } catch { /* already torn down */ }
    try { env.disconnect() } catch { /* already torn down */ }
  }

  if (typeof window !== 'undefined') {
    window.__MIC_CUE_COUNT__ = (window.__MIC_CUE_COUNT__ ?? 0) + 1
  }
  return true
}
